export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  avatar?: string;
  hasAvatar?: boolean;
  role: UserRole;
  hourlyRate?: number;
  currency?: string;
  loginStreak?: number;
  lastLoginDate?: string;
  totalEarnings?: number;
  pendingPayment?: number;
  monthlyHours?: number;
  emailVerified: boolean;
  isActive: boolean;
  isOnline?: boolean;
  lastActiveAt?: string;
  currentTimer?: {
    project: string;
    projectName?: string;
    task?: string;
    taskName?: string;
    startTime: string;
    elapsed: number;
  };
  customPermissions?: Permissions;
  createdAt: string;
  updatedAt: string;
}

export interface Permission {
  resource: string;
  actions: string[];
}

export interface PermissionSetting {
  mainCard?: boolean;
  sideMenu?: boolean;
}

export interface Permissions {
  dashboard?: PermissionSetting;
  projects?: PermissionSetting;
  tasks?: PermissionSetting;
  teams?: PermissionSetting;
  crm?: PermissionSetting;
  sales?: PermissionSetting;
  sourcing?: PermissionSetting;
  research?: PermissionSetting;
  timeTracking?: PermissionSetting;
  requests?: PermissionSetting;
  users?: PermissionSetting;
}

export enum UserRole {
  SUPERADMIN = 'superadmin',
  MANAGER = 'manager',
  MEMBER = 'member',
  MEDIA_BUYER = 'media_buyer',
  GUEST = 'guest',
}


export enum TaskStatus {
  TODO = 'todo',
  IN_PROGRESS = 'in_progress',
  IN_REVIEW = 'in_review',
  DONE = 'done',
}

export enum TaskPriority {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
  URGENT = 'urgent',
}

export interface Task {
  id: string;
  title: string;
  description?: string;
  status: TaskStatus;
  priority: TaskPriority;
  position: number;
  dueDate?: string;
  estimatedHours?: number;
  labels?: string[];
  attachments?: string[];
  assignee?: User;
  reporter: User;
  project: Project;
  column: TaskColumn;
  createdAt: string;
  updatedAt: string;
}

export interface TaskBoard {
  id: string;
  name: string;
  description?: string;
  isDefault: boolean;
  isActive: boolean;
  project: Project;
  columns: TaskColumn[];
  createdAt: string;
  updatedAt: string;
}

export interface TaskColumn {
  id: string;
  name: string;
  color?: string;
  position: number;
  wipLimit?: number;
  isActive: boolean;
  board: TaskBoard;
  tasks: Task[];
  createdAt: string;
  updatedAt: string;
}

export interface Project {
  id: string;
  name: string;
  description?: string;
  avatar?: string;
  status: ProjectStatus;
  startDate?: string;
  endDate?: string;
  isActive: boolean;
  owner?: User;
  createdBy?: User;
  team?: Team;
  members?: User[];
  tasks?: Task[];
  goals?: ProjectGoal[];
  createdAt: string;
  updatedAt: string;
}

export interface ProjectGoal {
  id: string;
  title: string;
  description?: string;
  targetDate?: string;
  isCompleted: boolean;
  project?: Project;
  createdAt: string;
  updatedAt: string;
}

export enum ProjectStatus {
  PLANNING = 'planning',
  ACTIVE = 'active',
  ON_HOLD = 'on_hold',
  COMPLETED = 'completed'
}

export interface Team {
  id: string;
  name: string;
  description?: string;
  avatar?: string;
  isActive: boolean;
  owner: User;
  members: User[];
  projects: Project[];
  createdAt: string;
  updatedAt: string;
}

export interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
}

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface SignupCredentials {
  email: string;
  firstName: string;
  lastName: string;
  password: string;
}

export interface ApiResponse<T> {
  data: T;
  message?: string;
  success: boolean;
}

export interface Request {
  id: string;
  title: string;
  description: string;
  category: 'equipment' | 'software' | 'training' | 'time_off' | 'other';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  status: 'pending' | 'approved' | 'rejected';
  submittedBy: User;
  submittedAt: string;
  reviewedBy?: User;
  reviewedAt?: string;
  reviewNotes?: string;
}

export interface PaymentRequest {
  id: string;
  user: User;
  hoursWorked: number;
  hourlyRate: number;
  totalAmount: number;
  month: string;
  status: 'pending' | 'approved' | 'paid';
  requestedAt: string;
  approvedAt?: string;
  paidAt?: string;
  approvedBy?: User;
  notes?: string;
}

export interface MediaBuyerStats {
  id: string;
  user: User;
  date: string;
  spend: number;
  revenue: number;
  profit: number;
  roas: number;
  ctr: number;
  cpm: number;
  conversions: number;
  notes?: string;
  createdAt: string;
}

export interface TimeEntry {
  id: string;
  user: User;
  project: string;
  date: string;
  hours: number;
  description?: string;
  createdAt: string;
  updatedAt: string;
}

export interface ShopifyShop {
  id: string;
  name: string;
  domain: string;
  isActive: boolean;
}

export interface ShopifyStats {
  id: string;
  shop: ShopifyShop;
  date: string;
  revenue: number;
  orders: number;
  visitors: number;
  conversionRate: number;
  averageOrderValue: number;
  returnRate: number;
  topProducts: Array<{
    name: string;
    revenue: number;
    units: number;
  }>;
  submittedBy: User;
  createdAt: string;
}

export enum AdPlatform {
  FACEBOOK = 'facebook',
  GOOGLE = 'google',
  TIKTOK = 'tiktok',
  SNAPCHAT = 'snapchat',
  OTHER = 'other',
}

export interface AdAccount {
  id: string;
  name: string;
  platform: AdPlatform;
  accountId?: string;
  isActive: boolean;
  shop?: Shop;
  shops?: Shop[];
  createdAt: string;
  updatedAt: string;
}

export interface Shop {
  id: string;
  name: string;
  description?: string;
  avatar?: string;
  url?: string;
  country?: string;
  currency: string;
  isActive: boolean;
  members: User[];
  adAccounts?: AdAccount[];
}

export enum NotificationType {
  TASK_ASSIGNED = 'task_assigned',
  TASK_UPDATED = 'task_updated',
  TASK_COMMENT = 'task_comment',
  PROJECT_INVITE = 'project_invite',
  TEAM_INVITE = 'team_invite',
  OPPORTUNITY_UPDATED = 'opportunity_updated',
  MENTION = 'mention',
}

export interface Notification {
  id: string;
  type: NotificationType;
  title: string;
  message?: string;
  data?: Record<string, any>;
  isRead: boolean;
  actionUrl?: string;
  user: User;
  createdAt: string;
  updatedAt: string;
}

export interface Label {
  id: string;
  name: string;
  color: string;
  backgroundColor: string;
  borderColor: string;
  createdAt: string;
  updatedAt: string;
}